<template>
  <AppLink :to="getLink(slide)" class="card">
    <img :src="slide.imageUrl" :alt="slide.linkText" :fetchpriority="fetchPriority" :loading="imageLoading" />
    <div class="card-content">
      <div class="title" v-html="slide?.title" />
      <UiButton
        class="season-button"
        :text="slide?.linkText || t('viewExperiences')"
        :variant="UiButtonVariant.SECONDARY"
        :color-mode="UiButtonColorMode.INVERTED"
        :size="UiButtonSize.S"
        :href="seasonUrl"
      />
    </div>
  </AppLink>
</template>

<script setup lang="ts">
import { UiButton, UiButtonSize, UiButtonColorMode, UiButtonVariant } from '@experience/core-button-ui';
import { useI18n } from '@experience/i18n';
import { computed } from 'vue';

import { absoluteSeasonUrl } from '@/shared/helpers/urls';
import ru from '@/shared/i18n/ru';
import { type SeasonSlide } from '@/shared/models/season';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const props = defineProps<{
  slide: SeasonSlide;
  index: number;
}>();

// Первые 3 карточки видны на первом экране: грузим их eager, приоритет первой — high (LCP),
// остальным даём low/lazy, чтобы не конкурировать за трафик.
const fetchPriority = computed(() => {
  if (props.index === 0) return 'high';
  return props.index < 3 ? 'auto' : 'low';
});

const imageLoading = computed(() => (props.index < 3 ? 'eager' : 'lazy'));

// Нормализуем URL сезонной карточки, добавляя завершающий слеш только при его отсутствии.
const seasonUrl = computed(() => (props.slide.url.endsWith('/') ? props.slide.url : `${props.slide.url}/`));

const getLink = (slide: SeasonSlide) => absoluteSeasonUrl(slide.url);
</script>

<style lang="scss" scoped>
a {
  text-decoration: none;
}

.card {
  position: relative;
  display: flex;
  align-items: flex-end;
  aspect-ratio: 3/4;
  padding: var(--alias-space-16, 16px);
  overflow: hidden;
  border-radius: 16px;

  &::after {
    position: absolute;
    inset: 0;
    z-index: 2;
    width: 100%;
    height: 100%;
    content: '';
    background: linear-gradient(180deg, rgb(0 0 0 / 0%) 50.45%, rgb(0 0 0 / 80%) 80.31%);
  }
}

img {
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.card-content {
  position: relative;
  z-index: 3;
  color: var(--alias-color-text-inverse);
}

.title {
  /* stylelint-disable-next-line declaration-property-value-keyword-no-deprecated */
  word-break: break-word;

  @include typography.base-400-300-semi;
}

.card:hover img {
  transform: scale(1.1);
}

.season-button {
  margin-top: var(--alias-space-12, 12px);
}
</style>
